import { Transition, Teleport, computed, defineComponent, h, inject, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue';
import mitt from 'mitt';

const MODAL_EMITTER = Symbol('tpj-modal-emitter');
const activeModals = new Set();

function formatSize(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (typeof value === 'number') {
        return `${value}px`;
    }

    const trimmed = `${value}`.trim();
    if (/^\d+$/.test(trimmed)) {
        return `${trimmed}px`;
    }

    return trimmed;
}

function updateBodyScroll() {
    if (typeof document === 'undefined') {
        return;
    }

    if (activeModals.size > 0) {
        document.body.classList.add('v--modal-block-scroll');
    } else {
        document.body.classList.remove('v--modal-block-scroll');
    }
}

const ModalWrapper = defineComponent({
    name: 'ModalWrapper',
    inheritAttrs: false,
    props: {
        name: {
            type: String,
            required: true,
        },
        width: {
            type: [Number, String],
            default: null,
        },
        height: {
            type: [Number, String],
            default: null,
        },
        scrollable: {
            type: Boolean,
            default: false,
        },
        clickToClose: {
            type: Boolean,
            default: true,
        },
        adaptive: {
            type: Boolean,
            default: true,
        },
        shiftY: {
            type: [Number, String],
            default: 0,
        },
    },
    emits: ['before-open', 'opened', 'before-close', 'closed'],
    setup(props, { slots, emit }) {
        const attrs = useAttrs();
        const emitter = inject(MODAL_EMITTER);
        const isActive = ref(false);
        const payload = ref(undefined);

        const applyShift = computed(() => {
            if (props.shiftY === null || props.shiftY === undefined) {
                return null;
            }

            if (typeof props.shiftY === 'number') {
                return `${props.shiftY * 100}%`;
            }

            const parsed = `${props.shiftY}`.trim();
            if (parsed === '') {
                return null;
            }

            return parsed;
        });

        const modalStyles = computed(() => {
            const style = {};
            if (attrs && attrs.style && typeof attrs.style === 'object') {
                Object.assign(style, attrs.style);
            }

            const formattedWidth = formatSize(props.width);
            const formattedHeight = formatSize(props.height);

            if (formattedWidth) {
                if (props.adaptive) {
                    style.maxWidth = formattedWidth;
                } else {
                    style.width = formattedWidth;
                }
            }

            if (formattedHeight) {
                style.height = formattedHeight;
            }

            if (props.scrollable) {
                style.overflowY = 'auto';
            }

            // Match vue-js-modal positioning behavior - calculate pixel values
            if (typeof window !== 'undefined') {
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                
                // Get modal width (use prop value or default)
                const modalWidth = typeof props.width === 'number' ? props.width : 
                    (typeof props.width === 'string' ? parseInt(props.width, 10) : 560);
                
                // Calculate horizontal center
                const left = Math.max(0, (viewportWidth - modalWidth) / 2);
                style.left = `${left}px`;
                style.width = `${modalWidth}px`;
                
                // Calculate vertical position based on shiftY
                // shiftY: 0 = top, 0.5 = center, 1 = bottom
                const shiftY = parseFloat(props.shiftY) || 0.5;
                const modalHeight = typeof props.height === 'number' ? props.height :
                    (props.height === 'auto' ? viewportHeight * 0.8 : parseInt(props.height, 10) || viewportHeight * 0.8);
                
                // vue-js-modal formula: top = (viewportHeight - modalHeight) * shiftY
                const availableSpace = viewportHeight - modalHeight;
                const top = Math.max(0, availableSpace * shiftY);
                style.top = `${top}px`;
            }

            return style;
        });

        const overlayClasses = computed(() => {
            const classes = ['v--modal-overlay'];
            if (isActive.value) {
                classes.push('v--modal-overlay--active');
            }
            if (props.scrollable) {
                classes.push('v--modal-scrollable');
            }

            if (attrs && attrs.class) {
                classes.push(attrs.class);
            }

            return classes;
        });

        const boxClasses = computed(() => {
            const classes = ['v--modal-box', 'v--modal'];
            if (props.scrollable) {
                classes.push('v--modal-box--scrollable');
            }
            return classes;
        });

        const show = (params = {}) => {
            // Wrap params in event object to maintain Vue 2 vue-js-modal API compatibility
            // Components expect event.params.xxx format
            const event = { params };
            emit('before-open', event);
            payload.value = params;
            isActive.value = true;
            activeModals.add(props.name);
            updateBodyScroll();
            nextTick(() => emit('opened', event));
        };

        const hide = (params = payload.value || {}) => {
            if (!isActive.value) {
                return;
            }

            // Wrap params in event object to maintain Vue 2 vue-js-modal API compatibility
            const event = { params };
            emit('before-close', event);
            isActive.value = false;
            activeModals.delete(props.name);
            updateBodyScroll();
            nextTick(() => emit('closed', event));
        };

        const toggle = (params = payload.value || {}) => {
            if (isActive.value) {
                hide(params);
            } else {
                show(params);
            }
        };

        const onShow = ({ name, params }) => {
            if (name === props.name) {
                show(params);
            }
        };

        const onHide = ({ name, params }) => {
            if (name === props.name) {
                hide(params);
            }
        };

        const onToggle = ({ name, params }) => {
            if (name === props.name) {
                toggle(params);
            }
        };

        const onHideAll = ({ params }) => {
            hide(params);
        };

        const onKeydown = (event) => {
            if (event.key === 'Escape') {
                hide({ reason: 'escape' });
            }
        };

        const overlayClick = (event) => {
            if (!props.clickToClose) {
                return;
            }

            if (event.target === event.currentTarget) {
                hide({ reason: 'overlay' });
            }
        };

        onMounted(() => {
            emitter.on('show', onShow);
            emitter.on('hide', onHide);
            emitter.on('toggle', onToggle);
            emitter.on('hideAll', onHideAll);
        });

        onBeforeUnmount(() => {
            emitter.off('show', onShow);
            emitter.off('hide', onHide);
            emitter.off('toggle', onToggle);
            emitter.off('hideAll', onHideAll);
            activeModals.delete(props.name);
            updateBodyScroll();
            window.removeEventListener('keydown', onKeydown);
        });

        watch(isActive, (visible) => {
            if (visible) {
                window.addEventListener('keydown', onKeydown);
            } else {
                window.removeEventListener('keydown', onKeydown);
            }
        });

        return () => h(Teleport, { to: 'body' }, [
            h(Transition, { name: 'v--modal-fade' }, () =>
                isActive.value
                    ? h('div', {
                        class: overlayClasses.value,
                        style: attrs && typeof attrs.style === 'string' ? attrs.style : undefined,
                        'data-modal': props.name,
                        onClick: overlayClick,
                    }, [
                        h('div', {
                            class: boxClasses.value,
                            style: modalStyles.value,
                        }, slots.default ? slots.default({ params: payload.value }) : []),
                    ])
                    : null
            ),
        ]);
    },
});

const emitter = mitt();

export const modalService = {
    show(name, params = {}) {
        emitter.emit('show', { name, params });
    },
    hide(name, params = {}) {
        emitter.emit('hide', { name, params });
    },
    toggle(name, params = {}) {
        emitter.emit('toggle', { name, params });
    },
    hideAll(params = {}) {
        emitter.emit('hideAll', { params });
    },
};

export const modalPlugin = {
    install(app) {
        app.component('modal', ModalWrapper);
        app.config.globalProperties.$modal = modalService;
        app.provide(MODAL_EMITTER, emitter);
    },
};

export function createModalPlugin() {
    return { plugin: modalPlugin, service: modalService };
}

